import React, { useState, useEffect, useRef } from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useTranslation } from "react-i18next";
import { Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

interface TooltipContentProps {
  objective: string;
  results: string[];
  phoneNumber: string;
  tag: string;
  onClose: () => void;
}

// ... (the rest of the code remains the same)

const TooltipContent: React.FC<TooltipContentProps> = ({
  objective,
  results,
  phoneNumber,
  tag,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <div className="testimony-container testimony-style-black">
      <div className="close-tooltip" onClick={onClose}>
        <FontAwesomeIcon icon={faTimes} />
      </div>
      <div className="testimony-tag">
        <Badge className="testimony-badge">{tag}</Badge>
      </div>
      <div className="testimony-content">
        <p>
          <strong>{t("objectives")}:</strong> {objective}
        </p>
        <strong>{t("results")}:</strong>
        <ul className="testimony-results-list">
          {results.map((result, index) => (
            <li key={index} className="testimony-result-item">
              {result}
            </li>
          ))}
        </ul>
      </div>
      <div className="testimony-footer">
        <a href={`tel:${phoneNumber}`} className="testimony-call-button">
          {t("callNow")}
        </a>
      </div>
    </div>
  );
};

interface ClientCardProps {
  src: string;
  alt: string;
  tooltip: Omit<TooltipContentProps, "onClose">;
  roundType: 1 | 2;
  styleType: "black" | "gradient";
}

const ClientCard: React.FC<ClientCardProps> = ({
  src,
  alt,
  tooltip,
  roundType,
  styleType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipId = `tooltip-${alt.replace(/\s+/g, "-")}`;
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (imgRef.current && !imgRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const containerClass = `testimony-container client-tooltip ${
    roundType === 1 ? "testimony-round-type-1" : "testimony-round-type-2"
  } ${
    styleType === "gradient"
      ? "testimony-style-gradient"
      : "testimony-style-black"
  }`;

  return (
    <div className="client-item">
      <img
        ref={imgRef}
        loading="lazy"
        decoding="async"
        src={src}
        alt={alt}
        data-tooltip-id={tooltipId}
        onClick={() => setIsOpen(!isOpen)}
      />
      <Tooltip
        id={tooltipId}
        place="bottom"
        render={() => (
          <TooltipContent {...tooltip} onClose={() => setIsOpen(false)} />
        )}
        isOpen={isOpen}
        className={containerClass}
      />
    </div>
  );
};

export default ClientCard;
